// Per-player Field Time Report data shaping.
//
// Builds everything the printable fairness report needs for ONE player:
// where they played, how much, infield vs outfield, bench time, and a
// game-by-game position log. Deliberately contains NO batting/pitching
// stats, no OVR/xOVR, and no other players — the report is a factual
// playing-time record a coach can hand a parent.
//
// Aggregation semantics mirror buildSeasonStats (src/lib/season-stats.ts): the
// same per-game source rule (fieldTimeRecordFor, which follows the team's "Field
// positions come from" setting), live inningLogs with BENCH/EH as bench, GC
// totals with SF→CF and bench = game innings − field innings, so this report can
// never disagree with the Stats tab heatmap.

import { format } from 'date-fns';
import { currentSeasonGames } from './utils';
import { gcInningsToDecimal, gameLengthInnings, fieldTimeRecordFor, fieldTimeSourceOf } from './gc-innings';

/** Canonical display order for the 11 real defensive positions. */
export const REPORT_POSITION_ORDER = ['P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'LCF', 'RCF'] as const;

export const POSITION_LABELS: Record<string, string> = {
  P: 'Pitcher', C: 'Catcher', '1B': 'First Base', '2B': 'Second Base',
  '3B': 'Third Base', SS: 'Shortstop', LF: 'Left Field', CF: 'Center Field',
  RF: 'Right Field', LCF: 'Left-Center', RCF: 'Right-Center',
};

const OUTFIELD = new Set(['LF', 'CF', 'RF', 'LCF', 'RCF']);
const INFIELD_BASES = new Set(['1B', '2B', '3B', 'SS']);

export interface GameFieldLogRow {
  gameId: string;
  /** e.g. "May 12" — pre-formatted so the page component stays dumb. */
  dateLabel: string;
  opponent: string;
  source: 'live' | 'gc';
  /** Game had field data but this player never appeared in it. */
  dnp: boolean;
  /**
   * The game's only record is a live log too short to describe it (the coach
   * started the live engine and stopped), and no GameChanger import has
   * replaced it. The row is shown so a parent counting games sees every date,
   * but it makes NO claim about whether this player took the field, and it is
   * excluded from the player's tracked totals.
   *
   * Never set once a GC import exists: GC's per-position innings are the
   * complete record of a finished game and always win over a partial log.
   */
  unrecorded?: boolean;
  /** Nonzero positions in canonical order, e.g. [{pos:'SS',innings:3}]. */
  positions: { pos: string; innings: number }[];
  benchInnings: number;
}

export interface FieldTimeReportData {
  playerName: string;
  playerNumber: string;
  teamName: string;
  logoURL: string | null;
  seasonLabel: string;
  generatedDate: string;
  /** Completed (non-practice, current-season) games. */
  totalTeamGames: number;
  /** Of those, games with any recorded field data. */
  gamesTracked: number;
  /** Games in which this player appears in the field data. */
  playerGamesWithData: number;
  /** Player's tracked innings: field + bench. */
  totalTrackedInnings: number;
  fieldInnings: number;
  buckets: { pitcher: number; catcher: number; infield: number; outfield: number; bench: number };
  /** Nonzero positions in canonical order. */
  positionInnings: { pos: string; innings: number }[];
  /** Fairness-rule split: infield includes P and C (matches Must Play Infield). */
  ifOf: { infield: number; outfield: number };
  hasGcGames: boolean;
  gameLog: GameFieldLogRow[];
}

const safeDateLabel = (raw: any): string => {
  const d = new Date(raw);
  return isNaN(d.getTime()) ? String(raw ?? '') : format(d, 'MMM d');
};

export function buildFieldTimeReport(player: any, team: any, allGames: any[]): FieldTimeReportData {
  const seasonGames = currentSeasonGames(
    (allGames || []).filter(g => !g.isPractice),
    team,
  )
    .filter(g => g.status === 'completed')
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const positionCounts: Record<string, number> = {};
  REPORT_POSITION_ORDER.forEach(pos => { positionCounts[pos] = 0; });

  let gamesTracked = 0;
  let hasGcGames = false;
  const gameLog: GameFieldLogRow[] = [];

  const source = fieldTimeSourceOf(team);

  seasonGames.forEach(game => {
    // Which record describes this game, exactly as in buildSeasonStats. By
    // default a GameChanger import supersedes the live game engine: GC counts
    // every defensive inning of a finished game, and preferring a partial log
    // once told a parent their kid sat for innings he actually played. A team on
    // "Dugout live games" flips that for any game whose live log covers the
    // whole game.
    //
    // A live log can also be a fragment: the coach taps End Inning once or twice
    // and then stops to actually coach. With no GC import, a log under half the
    // game (gameLengthInnings: the team's default, else 6) is an abandoned
    // attempt ('partial'). A player missing from it has NOT been shown to sit
    // out, and saying "Not in lineup" would be a false statement to a parent
    // about the one thing this report exists to answer, so say nothing instead.
    const record = fieldTimeRecordFor(game, source, team?.defaultInnings);
    if (record === 'none') return; // untracked — counts only toward totalTeamGames

    const rowBase = {
      gameId: game.id,
      dateLabel: safeDateLabel(game.date),
      opponent: game.opponent || 'Opponent',
    };

    if (record === 'partial') {
      gameLog.push({ ...rowBase, source: 'live', dnp: false, unrecorded: true, positions: [], benchInnings: 0 });
      return;
    }

    gamesTracked++;

    if (record === 'live') {
      const gamePositions: Record<string, number> = {};
      let bench = 0;
      let appeared = false;

      Object.values(game.inningLogs).forEach((inningLineup: any) => {
        const entry = (inningLineup as any[]).find(p => p.id === player.id);
        if (!entry) return;
        appeared = true;
        if (entry.position === 'BENCH' || entry.position === 'EH') {
          bench++;
        } else if (positionCounts[entry.position] !== undefined) {
          gamePositions[entry.position] = (gamePositions[entry.position] || 0) + 1;
        }
      });

      Object.entries(gamePositions).forEach(([pos, n]) => { positionCounts[pos] += n; });
      gameLog.push({
        ...rowBase,
        source: 'live',
        dnp: !appeared,
        positions: REPORT_POSITION_ORDER
          .filter(pos => gamePositions[pos] > 0)
          .map(pos => ({ pos, innings: gamePositions[pos] })),
        benchInnings: appeared ? bench : 0,
      });
      return;
    }

    // GC fallback — per-position totals only, no inning ordering.
    hasGcGames = true;
    const gcEntry = game.gcFieldTime[player.id];
    if (!gcEntry) {
      gameLog.push({ ...rowBase, source: 'gc', dnp: true, positions: [], benchInnings: 0 });
      return;
    }

    // Same game length as buildSeasonStats and every other screen.
    const gameInnings = gameLengthInnings(game, team?.defaultInnings);
    const gamePositions: Record<string, number> = {};
    let fieldTotal = 0;

    Object.entries(gcEntry).forEach(([pos, innings]: [string, any]) => {
      // Same rule as season-stats: GC writes innings as OUTS ("2.1" is 2 1/3).
      // Rounding per game understated every stint and dropped short ones entirely,
      // which on this report meant telling a parent their kid never played a
      // position he did play.
      const count = gcInningsToDecimal(innings);
      if (count <= 0) return;
      const mapped = pos === 'SF' ? 'CF' : pos; // short field → CF, matching season-stats
      if (positionCounts[mapped] === undefined) return;
      gamePositions[mapped] = (gamePositions[mapped] || 0) + count;
      fieldTotal += count;
    });

    Object.entries(gamePositions).forEach(([pos, n]) => { positionCounts[pos] += n; });
    gameLog.push({
      ...rowBase,
      source: 'gc',
      dnp: false,
      positions: REPORT_POSITION_ORDER
        .filter(pos => gamePositions[pos] > 0)
        .map(pos => ({ pos, innings: gamePositions[pos] })),
      benchInnings: Math.max(0, gameInnings - fieldTotal),
    });
  });

  // An unrecorded game makes no claim either way, so it counts toward neither
  // "games this player appeared in" nor his innings. It appears in the log only
  // so the date is not silently missing.
  const playerRows = gameLog.filter(r => !r.dnp && !r.unrecorded);
  const benchTotal = playerRows.reduce((sum, r) => sum + r.benchInnings, 0);
  const fieldInnings = REPORT_POSITION_ORDER.reduce((sum, pos) => sum + positionCounts[pos], 0);

  const sumWhere = (test: (pos: string) => boolean) =>
    REPORT_POSITION_ORDER.filter(test).reduce((sum, pos) => sum + positionCounts[pos], 0);

  return {
    playerName: player.name || '',
    playerNumber: player.number != null ? String(player.number) : '',
    teamName: team.name || '',
    logoURL: team.logoURL || null,
    seasonLabel: team.season && team.seasonYear != null
      ? `${team.season} ${team.seasonYear}`
      : `${new Date().getFullYear()}`,
    generatedDate: format(new Date(), 'MMMM d, yyyy'),
    totalTeamGames: seasonGames.length,
    gamesTracked,
    playerGamesWithData: playerRows.length,
    totalTrackedInnings: fieldInnings + benchTotal,
    fieldInnings,
    buckets: {
      pitcher: positionCounts.P,
      catcher: positionCounts.C,
      infield: sumWhere(pos => INFIELD_BASES.has(pos)),
      outfield: sumWhere(pos => OUTFIELD.has(pos)),
      bench: benchTotal,
    },
    positionInnings: REPORT_POSITION_ORDER
      .filter(pos => positionCounts[pos] > 0)
      .map(pos => ({ pos, innings: positionCounts[pos] })),
    // Fairness-rule split (fairness-check.ts): P and C count as infield.
    ifOf: {
      infield: sumWhere(pos => !OUTFIELD.has(pos)),
      outfield: sumWhere(pos => OUTFIELD.has(pos)),
    },
    hasGcGames,
    gameLog,
  };
}
